/**
 * @file
 * Keeps wide editor-authored tables from scrolling the whole page.
 *
 * Tables written in the WYSIWYG carry their own column count and content, so on
 * a phone their minimum width is regularly wider than the viewport. Left alone
 * they push the document wider than the screen and every other section of the
 * page ends up horizontally scrollable with it. Wrapping each table in a
 * scroll container confines that overflow to the table itself.
 *
 * The wrapper is only made focusable while it actually scrolls, so keyboard
 * users can reach the off-screen columns (WCAG 2.1.1) without collecting an
 * empty tab stop on the tables that already fit.
 */

(function (Drupal, once) {
  'use strict';

  const TABLE_SELECTOR = [
    '.field--type-text-long table',
    '.field--type-text-with-summary table'
  ].join(', ');

  const WRAPPER_CLASS = 'table-scroll';

  // Every wrapper on the page, so a viewport change can re-evaluate all of
  // them from the single resize listener registered below.
  const wrappers = [];

  /**
   * Build an accessible name for a table's scroll region.
   *
   * @param {HTMLElement} table - The table being wrapped.
   *
   * @return {string} The label to expose on the scroll container.
   */
  function labelFor(table) {
    const caption = table.querySelector('caption');
    const text = caption ? caption.textContent.trim() : '';

    return text || Drupal.t('Table');
  }

  /**
   * Expose the wrapper to keyboard users only while it can be scrolled.
   *
   * @param {HTMLElement} wrapper - The scroll container.
   */
  function updateFocusability(wrapper) {
    // A one pixel tolerance avoids toggling on sub-pixel rounding.
    if (wrapper.scrollWidth > wrapper.clientWidth + 1) {
      wrapper.setAttribute('tabindex', '0');
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', wrapper.dataset.tableLabel);
    }
    else {
      wrapper.removeAttribute('tabindex');
      wrapper.removeAttribute('role');
      wrapper.removeAttribute('aria-label');
    }
  }

  /**
   * Wrap a table in a scroll container, reusing one if it is already there.
   *
   * @param {HTMLElement} table - The table to wrap.
   *
   * @return {HTMLElement} The scroll container.
   */
  function wrapTable(table) {
    const parent = table.parentElement;

    if (parent && parent.classList.contains(WRAPPER_CLASS)) {
      // A wrapper that came from somewhere else carries no label of its own,
      // and an absent one would be read out as the string "undefined".
      parent.dataset.tableLabel = parent.dataset.tableLabel || labelFor(table);

      return parent;
    }

    const wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    // Assigned through the dataset rather than the attribute so the caption
    // text is stored as a string and never parsed as markup.
    wrapper.dataset.tableLabel = labelFor(table);
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);

    return wrapper;
  }

  /**
   * Re-evaluate every wrapper still on the page.
   */
  function refresh() {
    // Walk backwards so wrappers whose content has since been replaced by an
    // AJAX request can be dropped from the list rather than retained forever.
    for (let i = wrappers.length - 1; i >= 0; i--) {
      if (wrappers[i].isConnected) {
        updateFocusability(wrappers[i]);
      }
      else {
        wrappers.splice(i, 1);
      }
    }
  }

  // Rotating a phone or resizing a window changes whether a table still
  // overflows, so the tab stop has to be re-evaluated. One listener is
  // registered for the life of the page rather than one per behaviour run.
  window.addEventListener('resize', refresh);

  // Columns are measured during attach, while the page may still be showing
  // the fallback stack. Swapping in the webfont reflows them, and a table that
  // only overflows afterwards would otherwise keep a scroll area no keyboard
  // user could reach.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(refresh);
  }

  Drupal.behaviors.responsiveTables = {
    attach: function (context) {
      once('responsive-table', TABLE_SELECTOR, context).forEach(function (table) {
        const wrapper = wrapTable(table);
        wrappers.push(wrapper);
        updateFocusability(wrapper);
      });
    }
  };
})(Drupal, once);
